/**
 * Team Admin → Custom Stat Fields (FR-STATS-01..04).
 *
 * The registry screen: a sortable, searchable, filterable, paginated list of
 * the team's stat fields plus the create/edit form (with its non-dismissible
 * FERPA reminder) in a modal.
 *
 * Two behaviours are worth knowing:
 *   - **Retire vs delete.** Deleting is only possible for a field no play ever
 *     used. `DELETE` on a used field answers 409 `FIELD_IN_USE`; the service has
 *     already marked it inactive and we say so plainly instead of showing the
 *     raw conflict. Retired fields disappear from Play Entry and keep every
 *     historical value in the archive.
 *   - **Tier headroom.** `capacity` drives the "7 of 10 used" line, disables
 *     "New Custom Field" at the cap and points at the upgrade path (FR-STATS-02).
 */
import { useState } from 'react';

import { useApiClient } from '../../app/providers';
import { useToast } from '../../app/toast-context';
import { ConfirmDialog } from '../../components/confirm-dialog';
import { DataTable, type DataTableColumn } from '../../components/data-table';
import { OverlayDialog } from '../../components/overlay-dialog';
import { PageHeader } from '../../components/page-header';
import { StatusPill } from '../../components/status-pill';
import { ApiError } from '../../lib/api-error';
import {
  STAT_FIELD_APPLIES_TO,
  STAT_FIELD_DATA_TYPES,
  type StatFieldDefinition,
} from '../../lib/api-types';

import { CustomStatForm } from './custom-stat-form';
import { APPLIES_TO_LABELS, DATA_TYPE_LABELS } from './custom-stat-form-state';
import { useStatFields } from './use-stat-fields';

type OpenDialog =
  | { kind: 'none' }
  | { kind: 'create' }
  | { kind: 'edit'; field: StatFieldDefinition }
  | { kind: 'delete'; field: StatFieldDefinition };

const UNIT_FILTER_OPTIONS = [
  { value: 'all', label: 'All units' },
  ...STAT_FIELD_APPLIES_TO.map((unit) => ({ value: unit, label: APPLIES_TO_LABELS[unit] })),
];

const TYPE_FILTER_OPTIONS = [
  { value: 'all', label: 'All data types' },
  ...STAT_FIELD_DATA_TYPES.map((type) => ({ value: type, label: DATA_TYPE_LABELS[type] })),
];

export function CustomStatFieldsPage(): JSX.Element {
  const apiClient = useApiClient();
  const { showToast } = useToast();
  const controller = useStatFields();
  const [dialog, setDialog] = useState<OpenDialog>({ kind: 'none' });
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const { query, setQuery, fields, total, capacity, enumCategories, canWrite, isLoading, error } =
    controller;
  const { reload } = controller;
  const isAtLimit = capacity !== null && capacity.remaining === 0;

  async function removeField(field: StatFieldDefinition): Promise<void> {
    setDeleteError(null);
    try {
      await apiClient.removeStatField(field.id);
      setDialog({ kind: 'none' });
      showToast(`${field.label} has been deleted.`, 'success');
      reload();
    } catch (caught) {
      if (ApiError.isApiError(caught) && caught.status === 409) {
        // Delete-if-used: the service retired the field and kept the history.
        setDialog({ kind: 'none' });
        showToast(
          `${field.label} is used by existing plays, so it was marked inactive instead. ` +
            'Logged values stay in the archive and reports.',
          'info'
        );
        reload();
        return;
      }
      setDeleteError(
        ApiError.isApiError(caught)
          ? caught.message
          : 'The field could not be deleted. Please try again.'
      );
    }
  }

  async function setEnabled(field: StatFieldDefinition, isEnabled: boolean): Promise<void> {
    try {
      await apiClient.updateStatField(field.id, { is_enabled: isEnabled });
      showToast(
        isEnabled
          ? `${field.label} is active again and will appear on Play Entry.`
          : `${field.label} was retired. Historical values are kept.`,
        'success'
      );
      reload();
    } catch (caught) {
      showToast(
        ApiError.isApiError(caught) ? caught.message : 'The field could not be updated.',
        'error'
      );
    }
  }

  const columns: DataTableColumn<StatFieldDefinition>[] = [
    {
      id: 'label',
      header: 'Display Name',
      isSortable: true,
      isPinned: true,
      minWidth: 200,
      maxWidth: 380,
      grow: 3,
      render: (field) => (
        <span className="stat-cell" title={`${field.label} (${field.stat_key})`}>
          <span className="stat-cell__label">{field.label}</span>
          <span className="stat-cell__key mono">{field.stat_key}</span>
        </span>
      ),
      renderDetail: (field) => `${field.label} (${field.stat_key})`,
    },
    {
      id: 'data_type',
      header: 'Data Type',
      priority: 2,
      minWidth: 150,
      maxWidth: 240,
      grow: 1,
      render: (field) => (
        <span>
          {DATA_TYPE_LABELS[field.data_type]}
          {field.enum_category === null ? null : (
            <span className="muted"> · {field.enum_category}</span>
          )}
        </span>
      ),
    },
    {
      id: 'applies_to',
      header: 'Applies To',
      priority: 1,
      minWidth: 110,
      maxWidth: 160,
      grow: 0,
      render: (field) => APPLIES_TO_LABELS[field.applies_to],
    },
    {
      id: 'hudl',
      header: 'Hudl Column',
      priority: 4,
      // Mostly empty for new fields: opt in from the Columns picker.
      defaultVisible: false,
      minWidth: 130,
      grow: 1,
      render: (field) => field.hudl_export_key ?? '—',
    },
    {
      id: 'status',
      header: 'Status',
      priority: 3,
      minWidth: 200,
      maxWidth: 240,
      grow: 0,
      render: (field) => (
        <span className="badge-row stat-cell__pills">
          <StatusPill tone={field.is_enabled ? 'success' : 'neutral'}>
            {field.is_enabled ? 'Active' : 'Retired'}
          </StatusPill>
          {field.is_builtin ? <StatusPill>Built-in</StatusPill> : null}
          {field.is_required ? <StatusPill tone="info">Required</StatusPill> : null}
        </span>
      ),
    },
  ];

  const openCreate = (): void => setDialog({ kind: 'create' });
  const limitTitle = isAtLimit ? 'Your plan\u2019s custom field limit is reached.' : undefined;

  return (
    <section className="stack stat-fields">
      <PageHeader
        title="Custom Stat Fields"
        context={
          <>
            {total} {total === 1 ? 'field' : 'fields'} in this catalog
            {capacity === null
              ? ''
              : capacity.limit === null
                ? ' · unlimited custom fields on your plan'
                : ` · ${capacity.used} of ${capacity.limit} custom fields used`}
          </>
        }
        actions={
          canWrite ? (
            <button
              type="button"
              className="btn btn--primary"
              disabled={isAtLimit}
              title={limitTitle}
              onClick={openCreate}
            >
              New Custom Field
            </button>
          ) : undefined
        }
      />

      <DataTable<StatFieldDefinition>
        caption="Custom stat fields"
        surface="stat-fields"
        layoutName="Custom stat fields columns"
        columns={columns}
        rows={fields}
        getRowId={(field) => field.id}
        getRowClassName={(field) => (field.is_enabled ? undefined : 'data-table__row--inactive')}
        sort={{ by: query.sortBy, order: query.sortOrder }}
        onSortChange={(sort) =>
          setQuery({ sortBy: sort.by as typeof query.sortBy, sortOrder: sort.order })
        }
        search={{
          value: query.search,
          onChange: (value) => setQuery({ search: value }),
          label: 'Search fields',
          placeholder: 'Search name or code',
        }}
        filters={[
          {
            id: 'unit',
            label: 'Unit',
            value: query.unit,
            defaultValue: 'all',
            options: UNIT_FILTER_OPTIONS,
            onChange: (value) => setQuery({ unit: value as typeof query.unit }),
          },
          {
            id: 'type',
            label: 'Data type',
            value: query.dataType,
            defaultValue: 'all',
            options: TYPE_FILTER_OPTIONS,
            onChange: (value) => setQuery({ dataType: value as typeof query.dataType }),
          },
          {
            id: 'status',
            label: 'Status',
            value: query.isShowingRetired ? 'all' : 'active',
            options: [
              { value: 'active', label: 'Active only' },
              { value: 'all', label: 'Include retired' },
            ],
            onChange: (value) => setQuery({ isShowingRetired: value === 'all' }),
          },
          {
            id: 'origin',
            label: 'Origin',
            value: query.isShowingBuiltin ? 'all' : 'custom',
            options: [
              { value: 'custom', label: 'Custom only' },
              { value: 'all', label: 'Include built-ins' },
            ],
            onChange: (value) => setQuery({ isShowingBuiltin: value === 'all' }),
          },
        ]}
        pagination={{
          page: query.page,
          pageSize: query.pageSize,
          total,
          onPageChange: (page) => setQuery({ page }),
          onPageSizeChange: (pageSize) => setQuery({ pageSize }),
        }}
        isLoading={isLoading}
        error={error}
        onRetry={reload}
        emptyTitle="No custom stat fields yet"
        emptyDescription="Define a field to track something the built-in stats don't cover."
        emptyAction={
          canWrite ? (
            <button
              type="button"
              className="btn btn--primary btn--sm"
              disabled={isAtLimit}
              onClick={openCreate}
            >
              Create a Custom Field
            </button>
          ) : null
        }
        actionsWidth={canWrite ? 104 : 0}
        primaryRowAction={
          canWrite
            ? (field) => (
                <button
                  type="button"
                  className="btn btn--secondary btn--sm"
                  onClick={() => setDialog({ kind: 'edit', field })}
                >
                  Edit
                </button>
              )
            : undefined
        }
        rowMenu={
          canWrite
            ? (field) => [
                {
                  id: 'toggle',
                  label: field.is_enabled ? 'Retire' : 'Restore',
                  onSelect: () => void setEnabled(field, !field.is_enabled),
                },
                {
                  id: 'delete',
                  label: 'Delete',
                  tone: 'danger',
                  isDisabled: field.is_builtin,
                  disabledReason: 'Built-in fields cannot be deleted; retire them instead.',
                  onSelect: () => {
                    setDeleteError(null);
                    setDialog({ kind: 'delete', field });
                  },
                },
              ]
            : undefined
        }
        getRowMenuLabel={(field) => `More actions for ${field.label}`}
      />

      {dialog.kind === 'create' ? (
        <OverlayDialog
          title="New Custom Stat Field"
          onClose={() => setDialog({ kind: 'none' })}
          panelClassName="stat-field-dialog"
        >
          <CustomStatForm
            enumCategories={enumCategories}
            capacity={capacity}
            onCancel={() => setDialog({ kind: 'none' })}
            onSubmitCreate={async (input) => {
              const created = await apiClient.createStatField(input);
              setDialog({ kind: 'none' });
              showToast(`${created.label} is now available on Play Entry.`, 'success');
              reload();
            }}
          />
        </OverlayDialog>
      ) : null}

      {dialog.kind === 'edit' ? (
        <OverlayDialog
          title={`Edit ${dialog.field.label}`}
          onClose={() => setDialog({ kind: 'none' })}
          panelClassName="stat-field-dialog"
        >
          <CustomStatForm
            field={dialog.field}
            enumCategories={enumCategories}
            onCancel={() => setDialog({ kind: 'none' })}
            onSubmitEdit={async (patch) => {
              await apiClient.updateStatField(dialog.field.id, patch);
              setDialog({ kind: 'none' });
              showToast(`${dialog.field.label} updated.`, 'success');
              reload();
            }}
          />
        </OverlayDialog>
      ) : null}

      {dialog.kind === 'delete' ? (
        <ConfirmDialog
          title="Delete Custom Stat Field"
          tone="danger"
          confirmLabel="Delete Field"
          error={deleteError}
          consequence={`Delete "${dialog.field.label}" (${dialog.field.stat_key})? It will stop appearing on the Play Entry form, the archive columns and the Stat Tracker. If any play already records this field it cannot be deleted — it will be marked inactive instead and every logged value is kept.`}
          onCancel={() => setDialog({ kind: 'none' })}
          onConfirm={() => removeField(dialog.field)}
        />
      ) : null}
    </section>
  );
}
